import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import apiClient from '@/utils/apiClient';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, UserCheck, UserPlus, X } from 'lucide-react';

const StudentAssignment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const preselectedStudentId = searchParams.get('student_id');
  const assignmentType = searchParams.get('type') || 'warden'; // 'warden' or 'parent'

  const [students, setStudents] = useState([]);
  const [wardens, setWardens] = useState([]);
  const [parents, setParents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
    if (preselectedStudentId) {
      setSelectedStudents([preselectedStudentId]);
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch students
      const studentsRes = await apiClient.get(`/v1/admin/students/?hostel_id=${user.hostel_id}`);
      setStudents(Array.isArray(studentsRes.data) ? studentsRes.data : []);

      if (assignmentType === 'warden') {
        // Fetch wardens
        const wardensRes = await apiClient.get(`/v1/admin/wardens/?hostel_id=${user.hostel_id}`);
        setWardens(Array.isArray(wardensRes.data) ? wardensRes.data : []);
      } else {
        // Fetch parents
        const parentsRes = await apiClient.get(`/v1/admin/parents/?hostel_id=${user.hostel_id}`);
        setParents(Array.isArray(parentsRes.data) ? parentsRes.data : []);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleAssign = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    if (!selectedAssignee) {
      toast.error(`Please select a ${assignmentType}`);
      return;
    }

    setLoading(true);
    try {
      if (assignmentType === 'warden') {
        // Assign warden to students
        for (const studentId of selectedStudents) {
          await apiClient.put(`/v1/admin/students/${studentId}`, {
            assigned_warden_id: selectedAssignee
          });
        }
        toast.success(`Successfully assigned warden to ${selectedStudents.length} student(s)`);
      } else {
        // Assign parent to students
        for (const studentId of selectedStudents) {
          await apiClient.put(`/v1/admin/students/${studentId}`, {
            parent_id: selectedAssignee
          });
        }
        toast.success(`Successfully assigned parent to ${selectedStudents.length} student(s)`);
      }
      
      // Navigate back
      navigate('/admin/students');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.class_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const assignees = assignmentType === 'warden' ? wardens : parents;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/students')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Students
          </Button>
          <h1 className="text-2xl font-bold" style={{ color: '#2C5F7F' }}>
            {assignmentType === 'warden' ? 'Assign Warden' : 'Assign Parent'}
          </h1>
        </div>
      </div>

      {/* Assignment Type Toggle */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/admin/student-assignment?type=warden')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
              assignmentType === 'warden'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <UserCheck className="w-5 h-5 inline-block mr-2" />
            Assign Warden
          </button>
          <button
            onClick={() => navigate('/admin/student-assignment?type=parent')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
              assignmentType === 'parent'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <UserPlus className="w-5 h-5 inline-block mr-2" />
            Assign Parent
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Students Selection */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4" style={{ color: '#2C5F7F' }}>
            Select Students ({selectedStudents.length} selected)
          </h2>
          
          {/* Search */}
          <input
            type="text"
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* Students List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredStudents.map(student => (
              <div
                key={student.id}
                onClick={() => handleStudentToggle(student.id)}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedStudents.includes(student.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{student.name}</p>
                    <p className="text-sm text-gray-600">
                      {student.class_name} • Room {student.room_number}
                    </p>
                  </div>
                  {selectedStudents.includes(student.id) && (
                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Assignee Selection */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4" style={{ color: '#2C5F7F' }}>
            Select {assignmentType === 'warden' ? 'Warden' : 'Parent'}
          </h2>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {assignees.map(assignee => (
              <div
                key={assignee.id}
                onClick={() => setSelectedAssignee(assignee.id)}
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  selectedAssignee === assignee.id
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{assignee.name}</p>
                    <p className="text-sm text-gray-600">{assignee.phone}</p>
                    {assignmentType === 'warden' && assignee.assigned_student_count !== undefined && (
                      <p className="text-xs text-gray-500 mt-1">
                        Currently managing {assignee.assigned_student_count} student(s)
                      </p>
                    )}
                  </div>
                  {selectedAssignee === assignee.id && (
                    <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/admin/students')}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          disabled={loading || selectedStudents.length === 0 || !selectedAssignee}
          className="btn-primary text-white"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Assigning...
            </>
          ) : (
            <>
              {assignmentType === 'warden' ? <UserCheck className="w-4 h-4 mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Assign {assignmentType === 'warden' ? 'Warden' : 'Parent'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default StudentAssignment;
